import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import getShareUrl from '../../utils/getShareUrl';
import { setJGRadioCue } from './hitLabRadioCue';
import { shouldShowJGRadioDock } from './jgRadioDockVisibility';
import HitLabListenResult from './HitLabListenResult';
import RichShareButton from './RichShareButton';
import { getHitLabJourneyId } from './hitLabTelemetry';
import './hitLabResultShare.css';

export const EXAMPLE_SHARE_ID = 'example-uneekint-faith';
export const resultShareUrl = (id) => getShareUrl('hitlab-result', id);
export const loadSharedResult = async (id, signal) => {
  const url = resultShareUrl(id).replace(
    '/share/hitlab-result/',
    '/share/resolve/hitlab-result/',
  );
  const response = await fetch(url, { signal, cache: 'no-store' });
  if (!response.ok) throw new Error('This shared result is unavailable.');
  return (await response.json()).data;
};
export const resultShareEvent = (id, eventName) => {
  if (!id) return;
  try {
    const journeyId = getHitLabJourneyId();
    const seen = `jg:result-share:${eventName}:${id}:${journeyId}`;
    if (sessionStorage.getItem(seen)) return;
    sessionStorage.setItem(seen, '1');
    void fetch(`${resultShareUrl(id)}/events`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventName, journeyId }),
    }).catch(() => {});
  } catch {
    /* Optional telemetry never blocks a result or action. */
  }
};
export const reportShareAction = (id, action) => {
  const names = {
    native: 'native_intent',
    copy: 'copy_intent',
    cancelled: 'cancelled',
    failed: 'failed',
  };
  if (names[action])
    resultShareEvent(id, `hitlab_result_share_${names[action]}`);
};

export function SharedResultPresentation({
  data,
  preview = false,
  imageUrl,
  compact = false,
}) {
  const { player } = useContext(AuthContext);
  const location = useLocation();
  const [message, setMessage] = useState('');
  if (!data) return null;
  const live = data.rotation?.state === 'live';
  const canCueRadio = shouldShowJGRadioDock({
    pathname: location.pathname,
    isAuthenticated: Boolean(player),
  });
  return (
    <div
      className={`hitlab-paid-experience shared-result${
        compact ? ' shared-result--compact' : ''
      }`}
    >
      {preview && (
        <p className="sharing-preview-label">
          {data.enabled
            ? 'OWNER PREVIEW · Your public link is enabled'
            : 'PRIVATE PREVIEW · Visible only to you until you enable sharing'}
        </p>
      )}
      <HitLabListenResult
        result={data.result}
        editorial={data.editorial}
        catalogAvailable={live}
        catalogUnknown={data.rotation?.state === 'unknown'}
      />
      <div className="shared-result-actions">
        <p className="share-availability">
          {live
            ? 'Currently in JG Radio rotation'
            : data.rotation?.state === 'unknown'
            ? 'Current Radio availability could not be checked.'
            : 'Not currently in JG Radio rotation'}
        </p>
        {live &&
          (canCueRadio ? (
            <button
              className="primary"
              onClick={() => {
                setJGRadioCue({
                  trackId: data.rotation.trackId,
                  source: 'hitlab_result_share',
                });
                setMessage('Song cued in Radio. Press Play when you’re ready.');
              }}
            >
              Listen to the song
            </button>
          ) : (
            <Link
              className="primary"
              to={{
                pathname: data.rotation.listenPath.split('?')[0],
                search: data.rotation.listenPath.includes('?')
                  ? `?${data.rotation.listenPath.split('?')[1]}`
                  : '',
                state:
                  preview && location.pathname === '/hitlab/submit'
                    ? { hitlabResultVersion: data.result.version }
                    : undefined,
              }}
            >
              Listen to the song
            </Link>
          ))}
        <p role="status">{message}</p>
      </div>
      {imageUrl && (
        <details className="result-social-preview">
          <summary>See the social link preview</summary>
          <img
            src={imageUrl}
            alt={`${data.result.songTitle} by ${data.result.artistName} — Joe’s Listen Result social preview`}
          />
          <p className="share-note">
            An exact excerpt from this result. Apps may display link previews
            differently.
          </p>
        </details>
      )}
      {!preview && (
        <footer className="share-acquisition">
          <p>Got a record of your own?</p>
          <Link
            to={`/hitlab?src=result-share&ref=${encodeURIComponent(data.id)}`}
            onClick={() =>
              resultShareEvent(data.id, 'hitlab_result_share_referral_clicked')
            }
          >
            Put it in JG HitLab →
          </Link>
        </footer>
      )}
    </div>
  );
}

export default function HitLabSharedResult() {
  const { player } = useContext(AuthContext);
  const location = useLocation();
  const { id } = useParams();
  const [data, setData] = useState(null),
    [error, setError] = useState('');
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError('');
    loadSharedResult(id, abort.signal)
      .then((result) => {
        setData(result);
        if (document.visibilityState === 'visible')
          resultShareEvent(id, 'hitlab_result_share_visited');
      })
      .catch((e) => {
        if (e.name !== 'AbortError') setError(e.message);
      });
    return () => abort.abort();
  }, [id]);
  return (
    <main
      className={`result-public-page${
        player ? ' result-public-page--authenticated' : ''
      }`}
    >
      <Link className="result-home-link" to="/hitlab">
        JG HITLAB
      </Link>
      {location.state?.fromHitlabLanding && (
        <p>
          <Link
            className="result-home-link"
            to={{
              pathname: '/hitlab',
              search:
                typeof location.state.hitlabLandingSearch === 'string'
                  ? location.state.hitlabLandingSearch
                  : '',
              hash: '#hitlab-listen-result',
            }}
          >
            Back to JG HitLab →
          </Link>
        </p>
      )}
      {location.state?.fromHitlabOffer && (
        <p>
          <Link className="result-home-link" to="/hitlab/intake">
            Continue with my record →
          </Link>
        </p>
      )}
      {error ? (
        <div role="alert">
          <h1>This shared result is unavailable.</h1>
          <p>The artist may have disabled the link.</p>
        </div>
      ) : !data ? (
        <p role="status">Loading Listen Result…</p>
      ) : (
        <>
          <SharedResultPresentation data={data} imageUrl={data.imageUrl} />
          <RichShareButton
            label="Share this result"
            title={`${data.result.songTitle} · JG HitLab`}
            text={`Joe Galaxy’s Listen Result for ${data.result.artistName}`}
            url={data.url}
            onAction={(action) => reportShareAction(id, action)}
          />
        </>
      )}
    </main>
  );
}
